import { ChevronDown, ChevronUp, GripVertical, Trash2 } from 'lucide-react'
import { useTranslation } from '@/hooks/use-translation'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import {
  ChoiceOptions,
  RatingSettings,
  CtaSettings,
} from '@/features/forms-and-surveys/components/question-settings'
import {
  QUESTION_TYPES,
  getQuestionTypeLabels,
} from '@/features/forms-and-surveys/constants'
import {
  SurveyQuestionType,
  SurveyUiTemplate,
} from '@/features/forms-and-surveys/types'

export interface QuestionFormData {
  index: number
  text: string
  type: SurveyQuestionType
  isRequired: boolean
  isActive?: boolean
  options?: string[]
  minValue?: number
  maxValue?: number
  actionLabel?: string
  actionUrl?: string
  uiTemplate?: SurveyUiTemplate
}

// Error structure for question fields
interface QuestionFieldError {
  message: string
  type: string
}

export interface QuestionErrors {
  text?: QuestionFieldError
  type?: QuestionFieldError
  isRequired?: QuestionFieldError
  isActive?: QuestionFieldError
  options?: QuestionFieldError
  minValue?: QuestionFieldError
  maxValue?: QuestionFieldError
  actionLabel?: QuestionFieldError
  actionUrl?: QuestionFieldError
  uiTemplate?: QuestionFieldError
}

interface QuestionCardProps {
  question: QuestionFormData
  index: number
  errors?: QuestionErrors
  onUpdate: (index: number, data: Partial<QuestionFormData>) => void
  onDelete: (index: number) => void
  onMoveUp: (index: number) => void
  onMoveDown: (index: number) => void
  canMoveUp: boolean
  canMoveDown: boolean
}

export function QuestionCard({
  question,
  index,
  errors,
  onUpdate,
  onDelete,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
}: QuestionCardProps) {
  const { t } = useTranslation()
  const questionTypeLabels = getQuestionTypeLabels(t)

  const handleUpdate = (field: keyof QuestionFormData, value: unknown) => {
    onUpdate(index, { [field]: value })
  }

  const handleOptionsChange = (options: string[]) => {
    handleUpdate('options', options)
  }

  const handleMinValueChange = (value: number) => {
    handleUpdate('minValue', value)
  }

  const handleMaxValueChange = (value: number) => {
    handleUpdate('maxValue', value)
  }

  const handleUiTemplateChange = (template: SurveyUiTemplate) => {
    handleUpdate('uiTemplate', template)
  }

  const handleActionLabelChange = (value: string) => {
    handleUpdate('actionLabel', value)
  }

  const handleActionUrlChange = (value: string) => {
    handleUpdate('actionUrl', value)
  }

  // Initialize default values when question type changes
  const handleTypeChange = (newType: SurveyQuestionType) => {
    const updates: Partial<QuestionFormData> = { type: newType }

    // Set default values based on question type
    switch (newType) {
      case 'SingleChoice':
      case 'MultipleChoice':
        updates.options = question.options?.length
          ? question.options
          : ['Option 1', 'Option 2']
        break
      case 'Rating':
        updates.minValue = question.minValue ?? 1
        updates.maxValue = question.maxValue ?? 5
        updates.uiTemplate = question.uiTemplate ?? 'Default'
        break
      case 'CTA':
        updates.actionLabel = question.actionLabel || 'Learn More'
        updates.actionUrl = question.actionUrl || ''
        break
      default:
        // Clear type-specific fields for other types
        updates.options = []
        updates.minValue = undefined
        updates.maxValue = undefined
        updates.actionLabel = undefined
        updates.actionUrl = undefined
        updates.uiTemplate = 'Default'
    }

    onUpdate(index, updates)
  }

  return (
    <Card className='relative'>
      <CardHeader className='pb-3'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            <div className='flex items-center gap-1 text-muted-foreground'>
              <GripVertical className='h-4 w-4' />
              <span className='text-sm font-medium'>
                {t('surveys.question')} {index + 1}
              </span>
            </div>
          </div>

          <div className='flex items-center gap-1'>
            <Button
              type='button'
              variant='ghost'
              size='sm'
              onClick={() => onMoveUp(index)}
              disabled={!canMoveUp}
              title={t('surveys.moveUp')}
            >
              <ChevronUp className='h-4 w-4' />
            </Button>
            <Button
              type='button'
              variant='ghost'
              size='sm'
              onClick={() => onMoveDown(index)}
              disabled={!canMoveDown}
              title={t('surveys.moveDown')}
            >
              <ChevronDown className='h-4 w-4' />
            </Button>
            <Button
              type='button'
              variant='ghost'
              size='sm'
              onClick={() => onDelete(index)}
              title={t('surveys.deleteQuestion')}
              className='text-destructive hover:text-destructive'
            >
              <Trash2 className='h-4 w-4' />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className='space-y-4'>
        {/* Question Text */}
        <div className='space-y-2'>
          <Label htmlFor={`question-${index}-text`}>
            {t('surveys.questionText')} *
          </Label>
          <Input
            id={`question-${index}-text`}
            value={question.text}
            onChange={(e) => handleUpdate('text', e.target.value)}
            placeholder={t('surveys.questionTextPlaceholder')}
            className={errors?.text ? 'border-red-500' : ''}
          />
          {errors?.text && (
            <p className='text-sm text-red-600'>{errors.text.message}</p>
          )}
        </div>

        {/* Question Type */}
        <div className='space-y-2'>
          <Label htmlFor={`question-${index}-type`}>
            {t('surveys.questionType')}
          </Label>
          <Select value={question.type} onValueChange={handleTypeChange}>
            <SelectTrigger className={errors?.type ? 'border-red-500' : ''}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {QUESTION_TYPES.map((type) => (
                <SelectItem key={type} value={type}>
                  {questionTypeLabels[type]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors?.type && (
            <p className='text-sm text-red-600'>{errors.type.message}</p>
          )}
        </div>

        {/* Question Settings */}
        <div className='flex items-center justify-between'>
          <div className='flex items-center space-x-2'>
            <Switch
              id={`question-${index}-required`}
              checked={question.isRequired}
              onCheckedChange={(checked) => handleUpdate('isRequired', checked)}
            />
            <Label htmlFor={`question-${index}-required`}>
              {t('surveys.isRequired')}
            </Label>
          </div>

          <div className='flex items-center space-x-2'>
            <Switch
              id={`question-${index}-active`}
              checked={question.isActive ?? true}
              onCheckedChange={(checked) => handleUpdate('isActive', checked)}
            />
            <Label htmlFor={`question-${index}-active`}>
              {t('surveys.isActive')}
            </Label>
          </div>
        </div>

        {/* Type-specific settings */}
        {(question.type === 'SingleChoice' ||
          question.type === 'MultipleChoice') && (
          <ChoiceOptions
            options={question.options || []}
            onChange={handleOptionsChange}
            error={errors?.options?.message}
          />
        )}

        {question.type === 'Rating' && (
          <RatingSettings
            minValue={question.minValue ?? 1}
            maxValue={question.maxValue ?? 5}
            uiTemplate={question.uiTemplate ?? 'Default'}
            onMinValueChange={handleMinValueChange}
            onMaxValueChange={handleMaxValueChange}
            onUiTemplateChange={handleUiTemplateChange}
            minValueError={errors?.minValue?.message}
            maxValueError={errors?.maxValue?.message}
          />
        )}

        {question.type === 'CTA' && (
          <CtaSettings
            actionLabel={question.actionLabel || ''}
            actionUrl={question.actionUrl || ''}
            onActionLabelChange={handleActionLabelChange}
            onActionUrlChange={handleActionUrlChange}
            actionLabelError={errors?.actionLabel?.message}
            actionUrlError={errors?.actionUrl?.message}
          />
        )}
      </CardContent>
    </Card>
  )
}
